import { loadStripe, Stripe } from '@stripe/stripe-js';
import { trackPayment } from '@/lib/analytics';
import { IS_DEV } from '@/config/analytics';

// Lazy-loaded Stripe promise with proper error handling
let stripePromise: Promise<Stripe | null> | null = null;

// Price mapping for analytics
const PRICE_AMOUNTS: Record<string, number> = {
  'price_consultation': 99, // $99
  'price_booking': 300,     // $300
  'price_guide': 9.99,     // $9.99
  'price_meeting': 19.9,    // $19.9
};

// Get amount from price ID
function getAmountFromPriceId(priceId: string): number | undefined {
  return PRICE_AMOUNTS[priceId];
}

export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    const stripeApiKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    
    // Enhanced debugging
    console.log('Loading Stripe with key:', {
      available: !!stripeApiKey,
      length: stripeApiKey ? stripeApiKey.length : 0,
      prefix: stripeApiKey ? stripeApiKey.substring(0, 8) + '...' : 'undefined',
      fullKey: stripeApiKey ? '***' + stripeApiKey.substring(stripeApiKey.length - 8) : 'undefined'
    });
    
    if (!stripeApiKey || stripeApiKey === 'pk_test_your_publishable_key_here') {
      console.error('ERROR: VITE_STRIPE_PUBLISHABLE_KEY is not properly set');
      console.error('Expected format: pk_test_...');
      console.error('Current value:', stripeApiKey);
      return Promise.reject(new Error('Stripe API key is not configured. Check your environment variables.'));
    }
    
    stripePromise = loadStripe(stripeApiKey);
  }
  
  return stripePromise;
}

export async function createCheckoutSession(priceId: string, type: 'consultation' | 'booking' | 'guide' | 'meeting') {
  try {
    console.log('Creating checkout session:', { priceId, type });
    
    // Track payment initiation
    const amount = getAmountFromPriceId(priceId);
    trackPayment(type, 'initiated', amount);
    
    if (IS_DEV) {
      console.log('[Analytics] Payment initiated:', { type, priceId, amount });
    }
    
    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        type,
      }),
    });

    let errorData;
    try {
      errorData = await response.json();
    } catch (jsonError) {
      errorData = null;
      console.error('Failed to parse error response:', jsonError);
    }

    if (!response.ok) {
      // Enhanced error handling with structured error messages from backend
      const errorCode = errorData?.code || 'UNKNOWN_ERROR';
      let errorMessage = errorData?.message || errorData?.error || 'Failed to create checkout session';
      
      // User-friendly error messages based on error code
      const errorMessages: Record<string, string> = {
        'STRIPE_CONFIG_MISSING': 'Payment service is not configured. Please contact support.',
        'STRIPE_API_ERROR': 'Payment service error. Please try again or contact support.',
        'VALIDATION_ERROR': 'Invalid request. Please check your information and try again.',
        'INVALID_PRICE_ID': 'Invalid service selection. Please refresh the page and try again.',
        'INTERNAL_ERROR': 'An unexpected error occurred. Please try again later.',
      };
      
      const friendlyMessage = errorMessages[errorCode] || errorMessage;
      
      console.error('Checkout session API error:', { 
        status: response.status, 
        errorCode,
        errorMessage,
        friendlyMessage,
        details: errorData,
        url: response.url
      });
      
      // Track payment creation failure
      trackPayment(type, 'failed', amount, errorCode);
      
      if (IS_DEV) {
        console.log('[Analytics] Payment failed:', { type, errorCode });
      }
      
      throw new Error(friendlyMessage);
    }

    const session = errorData; // response.json() already parsed above
    
    if (!session || !session.id) {
      console.error('Invalid session response:', session);
      throw new Error('Invalid session data from server');
    }

    console.log('Checkout session created:', session.id);
    
    // Track successful session creation
    trackPayment(type, 'session_created', amount);
    
    if (IS_DEV) {
      console.log('[Analytics] Payment session created:', { type, sessionId: session.id });
    }
    
    return session;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    
    // Track generic error if not already tracked
    const amount = getAmountFromPriceId(priceId);
    trackPayment(type, 'error', amount, error instanceof Error ? error.message : 'unknown');
    
    // Re-throw with enhanced message if needed
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Failed to create checkout session. Please try again.');
    }
  }
}

export async function redirectToCheckout(sessionId: string) {
  try {
    console.log('Redirecting to Stripe checkout:', sessionId);
    
    const stripe = await getStripe();
    
    if (!stripe) {
      throw new Error('Stripe failed to initialize. Please refresh the page and try again.');
    }

    const { error } = await stripe.redirectToCheckout({
      sessionId,
    });

    if (error) {
      console.error('Stripe redirect error:', {
        type: error.type,
        code: error.code,
        message: error.message,
        fullError: error
      });
      
      // User-friendly error messages for Stripe errors
      let friendlyMessage = error.message;
      if (error.type === 'card_error' || error.code === 'card_declined') {
        friendlyMessage = 'Your card was declined. Please try a different card or contact your bank.';
      } else if (error.code === 'expired_card') {
        friendlyMessage = 'Your card has expired. Please use a different card.';
      } else if (error.code === 'incorrect_cvc') {
        friendlyMessage = 'The CVC code is incorrect. Please check your card details.';
      }
      
      // Track payment redirect error
      trackPayment('unknown', 'redirect_error', undefined, error.code);
      
      if (IS_DEV) {
        console.log('[Analytics] Payment redirect error:', { errorCode: error.code });
      }
      
      throw new Error(friendlyMessage);
    }

    console.log('Redirect to Stripe successful');
    
    // Track successful redirect (payment flow continues)
    trackPayment('unknown', 'redirect_success');
    
    if (IS_DEV) {
      console.log('[Analytics] Payment redirect successful');
    }
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
}

export async function handlePayment(type: 'consultation' | 'booking' | 'guide' | 'meeting') {
  const priceId = type === 'consultation'
    ? 'price_consultation'
    : type === 'booking'
    ? 'price_booking'
    : type === 'guide'
    ? 'price_guide'
    : 'price_meeting';

  console.log('Handling payment:', { type, priceId });
  
  try {
    const session = await createCheckoutSession(priceId, type);
    await redirectToCheckout(session.id);
    
    // Track successful payment flow start
    const amount = getAmountFromPriceId(priceId);
    trackPayment(type, 'flow_started', amount);
    
    if (IS_DEV) {
      console.log('[Analytics] Payment flow started:', { type, amount });
    }
  } catch (error) {
    console.error('Payment flow error:', {
      type,
      priceId,
      error: error instanceof Error ? error.message : String(error),
      timestamp: new Date().toISOString()
    });
    
    // Track payment flow error
    const amount = getAmountFromPriceId(priceId);
    trackPayment(type, 'flow_error', amount, error instanceof Error ? error.message : 'unknown');
    
    if (IS_DEV) {
      console.log('[Analytics] Payment flow error:', { type, error: error instanceof Error ? error.message : String(error) });
    }
    
    throw error;
  }
}

// Default export for backward compatibility - returns the promise
export default getStripe();
